/// <reference path="../../../../core/extensions/string/string.extension.d.ts" />
import { Component, computed, ElementRef, inject, input, OnInit, output, signal, untracked, ViewChild, effect } from '@angular/core';
import '../../../../core/extensions/string/string.extension';
import { MemberModel } from '../../../../core/models/data/member.model';
import { MemberService } from '../../../../core/services/member.service';
import { CommonModule } from '@angular/common';
import { PaginationComponent } from '../../../../parts/pagination/pagination.component';
import { FormsModule } from '@angular/forms';
import { MemberSearchFilterComponent } from "../member-search-filter/member-search-filter.component";
import { PaginatedListModel } from '../../../../core/models/service/paginated-list.model';
import { ErrorMessages, LocalStorageKeyConstants, MemberConstants, PaginationConstants } from '../../../../core/constants/constants';
import { MemberStatusActions, MemberStatuses, PropertyMemberTypes } from '../../../../core/enums/data.enums';
import { MemberListViewLocations } from '../../../../core/enums/base.enums';
import { MemberListSortOptions } from '../../../../core/enums/sort-options.enums';
import { AvatarConstants } from '../../../../core/constants/avatar.constants';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { InViewPortDirective } from '../../../../core/directives/in-view-port.directive';
import { ResponsiveTableDirective } from '../../../../core/directives/responsive-table/responsive-table.directive';
import { ResponsiveTableColumnDirective } from '../../../../core/directives/responsive-table/responsive-table-column.directive';
import { ConfirmationModalComponent } from "../../../../parts/confirmation-modal/confirmation-modal.component";
import { InfoModalComponent } from "../../../../parts/info-modal/info-modal.component";
import { SortDropdownComponent } from '../../../../parts/sort-dropdown/sort-dropdown/sort-dropdown';
import { MemberFormModalComponent } from "../member-form-modal/member-form-modal.component";
import { ItemSelectionPipe } from "../../../../core/pipes/item-selection/item-selection-pipe";
import { UserSessionService } from '../../../../core/services/user-session.service';
import { ProfileOrganizationScopeService } from '../../../../core/services/authorization/profile-organization-scope.service';
import { AuthorizationService } from '../../../../core/services/authorization/authorization.service';
import { AuthorizationStateService } from '../../../../core/services/authorization/authorization-state.service';
import { PermissionUtilities } from '../../../../core/utilities/permission.utilities';
import { MemberPermission } from '../../../../core/enums/permissions.enums';
import { ListConstants } from '../../../../core/constants/lists.constants';
import { PhonePipe } from "../../../../core/pipes/phone-number/phone-number.pipe";
import { AvatarComponent } from "../../../../parts/avatar/avatar.component";
import { CommonChecks } from '../../../../core/utilities/common-checks.utilities';
import { MemberUtilities } from '../../../../core/utilities/member.utilities';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { BootstrapService } from '../../../../core/services/bootstrap.service';
import { PropertiesService } from '../../../../core/services/property.service';
import { BulkActionRequest } from '../../../../core/models/service/bulk-action-request.model';
import { MembersFilter } from '../../../../core/filters/members.filter';
import { ModalTypes, ModalActions } from '../../../../core/enums/base.enums';
import { MemberBulkActions } from '../../../../core/enums/bulk-actions.enums';
import { NoValuePipe } from '../../../../core/pipes/no-value/no-value.pipe';
import { OrgDatePipe } from '../../../../core/pipes/org-date/org-date.pipe';
import { BaseListPageClass } from '../../../../core/base-classes/base-list-page-class';
import { ServiceResultModel } from '../../../../core/models/service/service-result.model';
import { BulkActionResponse } from '../../../../core/models/service/bulk-action-response.model';
import { MembersMessagingConstants } from '../../../../core/constants/messaging.constants';
import { StatusBadgeComponent } from '../../../../parts/status-badge/status-badge.component';
import { TooltipDirective } from '../../../../core/directives/tooltip.directive';
import { IfHasPermissionDirective } from '../../../../core/directives/if-has-permission/if-has-permission.directive';
import { PageTitleComponent } from '../../../../parts/page-title/page-title.component';

@Component({
  selector: 'ms-member-list',
  imports: [
    PageTitleComponent,
    FontAwesomeModule,
    PaginationComponent,
    FormsModule,
    MemberSearchFilterComponent,
    CommonModule,
    InViewPortDirective,
    ConfirmationModalComponent,
    InfoModalComponent,
    SortDropdownComponent,
    MemberFormModalComponent,
    ItemSelectionPipe,
    PhonePipe,
    AvatarComponent,
    RouterLink,
    NoValuePipe,
    OrgDatePipe,
    ResponsiveTableDirective,
    ResponsiveTableColumnDirective,
    StatusBadgeComponent,
    TooltipDirective,
    IfHasPermissionDirective
],
  templateUrl: './member-list.component.html',
  host: {"style":"display:contents"}
})
export class MemberListPage extends BaseListPageClass implements OnInit {
  //Component Data
  public memberList = signal<MemberModel[]>([]);
  
  public targetedMember?: MemberModel;
  
  public MemberStatuses = MemberStatuses;
  public MemberListViewLocations = MemberListViewLocations;
  public PropertyMemberTypes = PropertyMemberTypes;
  public MemberBulkActions = MemberBulkActions;
  public MemberStatusActions = MemberStatusActions;
  public MemberPermission = MemberPermission;

  public isSubmitting = false;
  
  public currentMemberListViewLocation = input<MemberListViewLocations>(MemberListViewLocations.MemberListPage);
  public activePropertyId = input<number>();
  public isParentEntityDeleted = input<boolean>(false);
  public filterRoleId = input<number | undefined>();
  public currentOwnerId = input<number | undefined>();
  public excludeMemberIds = input<number[]>([]);
  public addMemberRequested = output<{memberIds: number[], memberType: PropertyMemberTypes}>();
  
  //Ownership Transfer
  public memberSelectedForOwnershipTransfer = output<MemberModel>();
  public transferringOrgId = input<number | undefined>();
  // When true, the owner-selection modal renders an "assign owner" action instead of "transfer ownership".
  public isAssignOwnerFlow = input<boolean>(false);

  public memberStatusAction?: MemberStatusActions;
  public memberBulkAction?: MemberBulkActions;
 
  //Confirmation Modal
  public confirmationCancelFunction: Function = () => {};
  public confirmationConfirmFunction: Function = () => {};

  // Sort
  public memberListSortOptions = MemberListSortOptions;

  //Avatar
  public avatarSize = AvatarConstants.SIZE().MEMBER_LIST;
  public avatarShape = AvatarConstants.SHAPE().MEMBER_LIST;
  public avatarAdditionalAttributes = AvatarConstants.ADDITIONAL_ATTRIBUTES().MEMBER_LIST;

  public skeletonMembers: MemberModel[] = [];

  @ViewChild(PaginationConstants.DEFAULT_SCROLL_TO_ELEMENT_NAME().replace(/^#/, '')) jumpToElementRef!: ElementRef;
  @ViewChild(PaginationConstants.DEFAULT_SCROLL_TO_ELEMENT_MOBILE_NAME().replace(/^#/, '')) jumpToElementMobileRef!: ElementRef;

  @ViewChild('memberListConfirmationModalRef') memberListConfirmationModalRef!: ConfirmationModalComponent;
  @ViewChild('memberListInfoModalRef') memberListInfoModalRef!: InfoModalComponent;
  @ViewChild(MemberFormModalComponent) memberFormModalRef!: MemberFormModalComponent;

  private profileScope = inject(ProfileOrganizationScopeService, { optional: true });

  // The organization this list belongs to: the profile entity's own when rendered on an organization
  // or property profile, otherwise the active one.
  private scopedOrganizationId(): number | undefined {
    return PermissionUtilities.resolveOrganizationScope(undefined, this.profileScope, this.userSessionService).organizationId;
  }

  constructor(
    private memberService: MemberService,
    public userSessionService: UserSessionService,
    private bootstrapService: BootstrapService,
    private propertiesService: PropertiesService,
    private activatedRoute: ActivatedRoute,
    private authorizationService: AuthorizationService,
    private authorizationStateService: AuthorizationStateService
  ) {
    super();
    
    // Watch for filterRoleId changes in Transfer Ownership Modal
    effect(() => {
      const roleId = this.filterRoleId();
      const viewLocation = this.currentMemberListViewLocation();
      
      if (viewLocation === MemberListViewLocations.TransferOrganizationOwnershipModal && roleId !== undefined) {
        // Reload members when the roleId becomes available
        untracked(() => this.loadMembers());
      }
    });

    effect(() => {
      const org = this.userSessionService.activeOrganization();
      const viewLocation = this.currentMemberListViewLocation();

      if (org && this.userSessionService.hasInitializedOrganization() &&
          viewLocation === MemberListViewLocations.MemberListPage) {
        untracked(() => this.loadMembers(false));
      }
    });
  }
  
  public ngOnInit(): void {
    this.createSkeletonMembers();
    this.orderBy = this.userSessionService.membersOrderBy;
    this.sortOptions = [...ListConstants.MEMBERS_SORT_OPTIONS()];
    //Instead of loadMembers being called here we are calling it from the filter control
    // then firing it's filterApplied event since we are wanting default filters on the initial page load

    this.openAddModalFromQueryParams(this.activatedRoute, '#memberFormModal', () => this.memberFormModalRef.openMemberModal());
  }

  public onAddMemberClicked(): void {
    this.memberFormModalRef.openMemberModal();
  }

  private createSkeletonMembers(): void {
    this.skeletonMembers = Array.from({ length: this.take }, (_, index) => ({
      memberId: index,
      organizationId: -1,
      firstName: 'Loading',
      lastName: 'Member',
      emailAddress: 'loading@example.com',
      phoneNumber: '000-000-0000',
      status: MemberStatuses.Active,
      lastLogin: new Date(),
      avatarFileId: undefined
    } as MemberModel));
  }

  public get displayMembers(): MemberModel[] {
    const memberListValue = this.memberList();
    if (this.isLoading && (!memberListValue || memberListValue.length === 0)) {
      return this.skeletonMembers;
    }
    
    const excludeIds = this.excludeMemberIds();
    if (this.currentMemberListViewLocation() === MemberListViewLocations.ManageOrganizationAdminsModal && 
        excludeIds.length > 0) {
      return (memberListValue || []).filter(m => !excludeIds.includes(m.memberId));
    }
    
    return memberListValue || [];
  }

  public clearSearch(): void {
    this.userSessionService.membersFilter.searchTerm = "";
  }

  public async loadMembers(append: boolean = false): Promise<void> {
    this.isLoading = true;

    if (append === false) { this.resetPagination(); }

    await this.userSessionService.getOrganizationSettings();

    let filterToUse: MembersFilter;

    // Use a fresh, isolated filter for Transfer Ownership Modal
    if (this.currentMemberListViewLocation() === MemberListViewLocations.TransferOrganizationOwnershipModal) {
      filterToUse = {
        organizationIds: CommonChecks.isNullOrUndefined(this.transferringOrgId())
          ? []
          : [this.transferringOrgId()!],
        roleIds: this.filterRoleId() ? [this.filterRoleId()!] : [],
        // Assigning an initial owner is permitted for a member of ANY lifecycle status (e.g. a member who
        // was just Created and is not yet Active), so only restrict to Active when transferring existing ownership.
        // Soft-deleted members are never eligible for ownership, so exclude them explicitly — an empty
        // `statuses` array does NOT filter out the Deleted status on its own.
        statuses: this.isAssignOwnerFlow() ? [] : [MemberStatuses.Active],
        includeDeletedMembers: false,
        searchTerm: this.userSessionService.membersFilter.searchTerm || ''
      };
    } else if (this.currentMemberListViewLocation() === MemberListViewLocations.ManageOrganizationAdminsModal) {
      // For Manage Admins Modal, show members eligible for Admin assignment filtered by the specified role
      // (typically the member role). Admin can be granted before a member has fully activated so it's already
      // held on their first login, so Created/Invited/Expired are eligible alongside Active.
      filterToUse = {
        organizationIds: CommonChecks.isNullOrUndefined(this.transferringOrgId())
          ? []
          : [this.transferringOrgId()!],
        roleIds: this.filterRoleId() ? [this.filterRoleId()!] : [],
        statuses: [MemberStatuses.Created, MemberStatuses.Invited, MemberStatuses.Expired, MemberStatuses.Active],
        searchTerm: this.userSessionService.membersFilter.searchTerm || ''
      };
    } else {
      // Use the shared filter for all other locations
      // Clear filter arrays to prevent duplicates
      this.userSessionService.membersFilter.organizationIds = [];
      this.userSessionService.membersFilter.propertyIds = [];
      this.userSessionService.membersFilter.memberIds = [];

      // The organization on screen, not necessarily the active one. On the property profile's Add
      // Members modal this has to be the PROPERTY's organization — that modal deliberately does not
      // filter by propertyId (it is picking from all of that organization's members), so this filter is
      // the only thing scoping it, and the active organization's members were being offered for a
      // property in a different organization (DEV-2643). The Transfer Ownership and Manage Admins
      // branches above already take the organization explicitly via transferringOrgId.
      const organizationId = this.scopedOrganizationId();
      if (CommonChecks.isNullOrUndefined(organizationId) == false) {
        this.userSessionService.membersFilter.organizationIds = [organizationId!];
      }

      // Only filter by property when viewing the Property Profile Page (showing members attached to that property)
      // Do NOT filter by property when in AddMemberToPropertyModal (we want to show all org members to choose from)
      if (this.currentMemberListViewLocation() === MemberListViewLocations.PropertyProfilePage && this.activePropertyId()) {
        this.userSessionService.membersFilter.propertyIds = [this.activePropertyId()!];
      }

      // The user-selected role filter (DEV-2544) only applies on the Members list page, where its UI is
      // visible. Other shared-filter locations (property profile tab, add-member modal) get a copy with
      // roleIds stripped so they aren't silently role-filtered — without mutating the persisted selection.
      filterToUse = this.currentMemberListViewLocation() === MemberListViewLocations.MemberListPage
        ? this.userSessionService.membersFilter
        : { ...this.userSessionService.membersFilter, roleIds: [] };
    }

    const response: ServiceResultModel<PaginatedListModel<MemberModel>> = await this.memberService.searchMembers(filterToUse, this.orderBy, this.skip(), this.take);

    this.totalCount = response?.data?.total ?? 0;
    this.hasNext = response?.data?.hasNext ?? false;

    if (response?.data?.data) {
      if (append == true) {        
          this.memberList.set(this.memberList().concat(response.data?.data!));
        } else {      
          this.memberList.set(response.data?.data!);
          //Clear Selected Items
          this.isSelectAll = false;
          this.selectionItems.set([]);
      }
    }

    this.isLoading = false;
    this.calculateSelected();
  }

  public async loadMore(): Promise<void> {
    this.loadMembers(true);
  }

  public jumpToTop(element: string) : void {
    switch (element) {
      case PaginationConstants.DEFAULT_SCROLL_TO_ELEMENT_NAME():
        this.jumpToElementRef.nativeElement.scrollIntoView({ behavior: 'smooth' });
        break;

      case PaginationConstants.DEFAULT_SCROLL_TO_ELEMENT_MOBILE_NAME():
        this.jumpToElementMobileRef.nativeElement.scrollIntoView({ behavior: 'smooth' });
        break;
    }
  }

  public filterApplied(): void {
    this.loadMembers();
  }

  public toggleSortDirection(columnName: string): void {
    const columnSortMap: Record<string, [MemberListSortOptions, MemberListSortOptions]> = {
      "Name": [MemberListSortOptions.NameASC, MemberListSortOptions.NameDESC],
      "Status": [MemberListSortOptions.StatusASC, MemberListSortOptions.StatusDESC],
      "Phone Number": [MemberListSortOptions.PhoneNumberASC, MemberListSortOptions.PhoneNumberDESC],
      "Role": [MemberListSortOptions.MostPermissiveRoleASC, MemberListSortOptions.MostPermissiveRoleDESC],
      "Last Login": [MemberListSortOptions.LastLoginASC, MemberListSortOptions.LastLoginDESC]
    };

    const [ascOption, descOption] = columnSortMap[columnName]
    // fall back to default if columnName isn't present
     || [MemberListSortOptions.LastLoginASC, MemberListSortOptions.LastLoginDESC];

    let sortDirection: MemberListSortOptions;

    if (this.orderBy === descOption) {
      sortDirection = ascOption;
    } else if (this.orderBy === ascOption) {
      sortDirection = descOption;
    } else {
      sortDirection = ascOption;
    }

    this.setSortOrder(sortDirection);
  }

  public setSortOrder(memberListSortOption: string) {
    this.orderBy = memberListSortOption;
    this.userSessionService.membersOrderBy = this.orderBy;
    this.userSessionService.saveToLocalStorage(LocalStorageKeyConstants.MEMBERS_ORDERBY(), this.orderBy);
    this.filterApplied();
  }

  // #region Properties
  public onAddMembersToProperty(propertyMemberType: PropertyMemberTypes): void {
    const selectedMemberIds = this.isSelectAll
      ? this.memberList().filter(m => !this.selectionItems().includes(m.memberId)).map(m => m.memberId)
      : this.selectionItems().map(id => id);

    if (selectedMemberIds.length > 0) {
      this.addMemberRequested.emit({memberIds: selectedMemberIds, memberType: propertyMemberType});
    }
  }

  public detachMemberFromProperty(member: MemberModel): void {
    this.targetedMember = member;

    this.confirmationConfirmFunction = this.confirmDetach;
    this.confirmationCancelFunction = this.cancelDetach;

    this.resetConfirmationModal();
    this.memberListConfirmationModalRef.modalType.set(ModalTypes.Question);

    this.memberListConfirmationModalRef.confirmButtonText.set("Detach");

    const replacementData: Record<string, string | undefined> = {
      firstName: this.targetedMember?.firstName, 
      lastName: this.targetedMember?.lastName
    };
    
    this.memberListConfirmationModalRef.message.set(
      MembersMessagingConstants.MEMBERS_DETACH_FROM_PROPERTY_CONFIRMATION().replaceValuesWithObject(replacementData));
    
    this.bootstrapService.manageModal("#memberListConfirmationModal", ModalActions.Show);
  }

  public async confirmDetach(): Promise<void> {
    if (this.targetedMember && this.activePropertyId()) {
      this.isSubmitting = true;
      try {
        const result = await this.propertiesService.removeMemberFromProperty(
          this.activePropertyId()!,
          { memberIds: [this.targetedMember.memberId]});

        this.memberListInfoModalRef.message.set(result.message);
        
        if(result.success === true) {
          this.memberListInfoModalRef.modalType.set(ModalTypes.Success);
        } else {
          this.memberListInfoModalRef.modalType.set(ModalTypes.Failure);
        }

        this.bootstrapService.manageModal("#memberListConfirmationModal", ModalActions.Hide);
        this.bootstrapService.manageModal("#memberListInfoModal", ModalActions.Show);

        this.loadMembers();
      } catch (error) {
        console.error('Error detaching member from property:', error);
      } finally {
        this.isSubmitting = false;
      }
    }
  }

  public cancelDetach(): void {
    this.bootstrapService.manageModal("#memberListConfirmationModal", ModalActions.Hide);
  }
  // #endregion Properties

  // #region Transfer Ownership
  public selectMemberForOwnershipTransfer(member: MemberModel): void {
    this.memberSelectedForOwnershipTransfer.emit(member);
  }
  // #endregion Transfer Ownership

  // True when the row is the logged-in user's member record; used to hide self deactivate/delete.
  public isOwnAccount(member: MemberModel): boolean {
    return MemberUtilities.belongsToCurrentUser(member, this.userSessionService);
  }

  // Staff (user-level members.status/delete) may act on their own account; org-scoped members may not.
  public canDeactivateOwnAccount = computed<boolean>(() => PermissionUtilities.hasUserLevelPermission(
    MemberPermission.Status, this.authorizationService, this.authorizationStateService));
  public canDeleteOwnAccount = computed<boolean>(() => PermissionUtilities.hasUserLevelPermission(
    MemberPermission.Delete, this.authorizationService, this.authorizationStateService));

  // Per-member protection results, populated lazily as each row's Actions dropdown is opened —
  // avoids firing an authorization lookup for every row on list load (DEV-2541).
  private protectedMemberCache = signal<Map<number, boolean>>(new Map());

  // A member cannot be deactivated/deleted by a viewer whose authority level isn't stronger than
  // theirs. Stays false (not hidden) until onActionsDropdownOpened resolves the check for this row —
  // isCheckingProtection() below drives a loading state in the meantime so the action never renders
  // as falsely clickable; the backend's own authority check remains the actual safeguard regardless
  // (DEV-2541).
  public isProtectedFromModification(member: MemberModel): boolean {
    return this.protectedMemberCache().get(member.memberId) ?? false;
  }

  // True while this row's authorization check hasn't resolved yet (own account excluded — that
  // resolves synchronously with no fetch). Drives a disabled/spinner state on the action so it
  // never looks clickable before we actually know the answer.
  public isCheckingProtection(member: MemberModel): boolean {
    return !this.isOwnAccount(member) && !this.protectedMemberCache().has(member.memberId);
  }

  public async onActionsDropdownOpened(member: MemberModel): Promise<void> {
    if (this.isOwnAccount(member) || this.protectedMemberCache().has(member.memberId)) return;

    // Use the member's own organizationId, not the viewer's currently active organization — these
    // can diverge (e.g. a Staff viewer browsing an org they don't belong to), and the backend keys
    // its own authority check off the target member's organizationId specifically.
    const targetAuthorityLevel = await PermissionUtilities.fetchTargetMemberAuthorityLevel(
      member.userId, member.organizationId, this.authorizationService);

    const isProtected = PermissionUtilities.isProtectedFromMemberModification(
      targetAuthorityLevel, member.organizationId, this.authorizationStateService);

    this.protectedMemberCache.update(cache => new Map(cache).set(member.memberId, isProtected));
  }

  // #region Change MemberStatus
  public confirmMemberStatusUpdate(statusAction: MemberStatusActions, member: MemberModel): void {
    let actionText = "";
    switch (statusAction) {
      case MemberStatusActions.Activate: 
        actionText = "activate";
        break;
      case MemberStatusActions.Deactivate: 
        actionText = "deactivate";
        break;
      case MemberStatusActions.Suspend: 
        actionText = "suspend";
        break;
      case MemberStatusActions.Unsuspend: 
        actionText = "unsuspend";
        break;
    }

    const confirmationMessageData = {actionText: actionText, firstName: member.firstName, lastName: member.lastName};
    const confirmationMessage = MembersMessagingConstants.MEMBERS_UPDATE_STATUS_CONFIRMATION().replaceValuesWithObject(confirmationMessageData);
    
    this.setActivationConfirmationModalFunctions();
    
    this.targetedMember = member;
    this.memberStatusAction = statusAction;

    this.resetConfirmationModal();
    this.memberListConfirmationModalRef.modalType.set(ModalTypes.Question);
    this.memberListConfirmationModalRef.message.set(confirmationMessage);

    this.memberListConfirmationModalRef.enableNotes.set(true);

    this.bootstrapService.manageModal("#memberListConfirmationModal", ModalActions.Show);
  }

  public setActivationConfirmationModalFunctions() : void {
    this.confirmationConfirmFunction = this.confirmedMemberStatusConfirmation;
    this.confirmationCancelFunction = this.cancelMemberStatusConfirmation
  }

  public async confirmedMemberStatusConfirmation(note: string | undefined) {
    this.isSubmitting = true;
    const response = await this.memberService.updateMemberStatus(this.memberStatusAction!, this.targetedMember!, note);

    if (response.success === false) {
      //Error response received
      this.memberListInfoModalRef.modalType.set(ModalTypes.Failure);
    } else {
      //No error response
      this.memberListInfoModalRef.modalType.set(ModalTypes.Success);

      this.selectionItems.set([]);
      this.loadMembers();
    }

    this.isSubmitting = false;
    this.memberListInfoModalRef.message.set(response.message);
    this.bootstrapService.manageModal("#memberListInfoModal", ModalActions.Show);
    this.bootstrapService.manageModal("#memberListConfirmationModal", ModalActions.Hide);
  }

  public cancelMemberStatusConfirmation() {
    this.bootstrapService.manageModal("#memberListConfirmationModal", ModalActions.Hide);
  }
  // #endregion Change Member Status
 
  // #region Delete Member
  public confirmMemberDeletion(member: MemberModel): void {
    const confirmationMessageData = {
      firstName: member.firstName,
      lastName: member.lastName,
      expirationDays: MemberConstants.INVITATION_EXPIRATION_DAYS()
    };
    const confirmationMessage = MembersMessagingConstants.MEMBERS_DELETE_CONFIRMATION().replaceValuesWithObject(confirmationMessageData);
    
    this.setDeletionConfirmationModalFunctions();
    
    this.targetedMember = member;

    this.resetConfirmationModal();
    this.memberListConfirmationModalRef.modalType.set(ModalTypes.Question);
    this.memberListConfirmationModalRef.message.set(confirmationMessage);

    this.memberListConfirmationModalRef.enableNotes.set(true);

    this.bootstrapService.manageModal("#memberListConfirmationModal", ModalActions.Show);
  }

  public setDeletionConfirmationModalFunctions() : void {
    this.confirmationConfirmFunction = this.confirmedMemberDeletionConfirmation;
    this.confirmationCancelFunction = this.cancelMemberDeletionConfirmation
  }

  public async confirmedMemberDeletionConfirmation(note: string | undefined) {
    this.isSubmitting = true;
    const response = await this.memberService.deleteMember(this.targetedMember!.memberId, note);

    if (response.success === false) {
      //Error response received
      this.memberListInfoModalRef.modalType.set(ModalTypes.Failure);
    } else {
      //No error response
      this.memberListInfoModalRef.modalType.set(ModalTypes.Success);
      this.selectionItems.set([]);
      this.loadMembers();
    }

    this.isSubmitting = false;
    this.memberListInfoModalRef.message.set(response.message);
    this.bootstrapService.manageModal("#memberListInfoModal", ModalActions.Show);
    this.bootstrapService.manageModal("#memberListConfirmationModal", ModalActions.Hide);
  }

  public cancelMemberDeletionConfirmation() {
    this.bootstrapService.manageModal("#memberListConfirmationModal", ModalActions.Hide);
  }
  // #endregion Delete Member

  // #region Invite Member
  public confirmMemberInvite(member: MemberModel): void {    
    const confirmationMessageData = {
      firstName: member.firstName,
      lastName: member.lastName,
      expirationDays: MemberConstants.INVITATION_EXPIRATION_DAYS()
    };
    const confirmationMessage = MembersMessagingConstants.MEMBERS_SEND_INVITATION_CONFIRMATION().replaceValuesWithObject(confirmationMessageData);

    this.setInviteConfirmationModalFunctions();
    
    this.targetedMember = member;

    this.resetConfirmationModal();
    this.memberListConfirmationModalRef.modalType.set(ModalTypes.Question);
    this.memberListConfirmationModalRef.message.set(confirmationMessage);
    this.memberListConfirmationModalRef.confirmButtonText.set('Send Invitation');
    this.memberListConfirmationModalRef.cancelButtonText.set('Cancel');

    this.bootstrapService.manageModal("#memberListConfirmationModal", ModalActions.Show);
  }

  public setInviteConfirmationModalFunctions(): void {
    this.confirmationConfirmFunction = this.confirmedMemberInviteConfirmation;
    this.confirmationCancelFunction = this.cancelMemberInviteConfirmation;
  }

  public async confirmedMemberInviteConfirmation() {
    this.isSubmitting = true;
    const response = await this.memberService.sendMemberInvitationEmail(this.targetedMember!.memberId);

    if (response.success === false) {
      this.memberListInfoModalRef.modalType.set(ModalTypes.Failure);
    } else {
      this.memberListInfoModalRef.modalType.set(ModalTypes.Success);
      this.selectionItems.set([]);
      this.loadMembers();
    }

    this.isSubmitting = false;
    this.memberListInfoModalRef.message.set(response.message || 'Invitation sent successfully.');
    this.bootstrapService.manageModal("#memberListInfoModal", ModalActions.Show);
    this.bootstrapService.manageModal("#memberListConfirmationModal", ModalActions.Hide);
  }

  public cancelMemberInviteConfirmation() {
    this.bootstrapService.manageModal("#memberListConfirmationModal", ModalActions.Hide);
  }
  // #endregion Invite Member

  // #region Uninvite Member
  public confirmMemberUninvite(member: MemberModel): void {
    const confirmationMessageData = {
      firstName: member.firstName,
      lastName: member.lastName,
      expirationDays: MemberConstants.INVITATION_EXPIRATION_DAYS()
    };
    const confirmationMessage = MembersMessagingConstants.MEMBERS_UNINVITE_CONFIRMATION().replaceValuesWithObject(confirmationMessageData);
    
    this.setUninviteConfirmationModalFunctions();
    
    this.targetedMember = member;

    this.resetConfirmationModal();
    this.memberListConfirmationModalRef.modalType.set(ModalTypes.Question);
    this.memberListConfirmationModalRef.message.set(confirmationMessage);
    this.memberListConfirmationModalRef.confirmButtonText.set('Cancel Invitation');
    this.memberListConfirmationModalRef.cancelButtonText.set('Go Back');

    this.bootstrapService.manageModal("#memberListConfirmationModal", ModalActions.Show);
  }

  public setUninviteConfirmationModalFunctions(): void {
    this.confirmationConfirmFunction = this.confirmedMemberUninviteConfirmation;
    this.confirmationCancelFunction = this.cancelMemberUninviteConfirmation;
  }

  public async confirmedMemberUninviteConfirmation() {
    this.isSubmitting = true;
    const response = await this.memberService.cancelMemberInvitation(this.targetedMember!.memberId);

    if (response.success === false) {
      this.memberListInfoModalRef.modalType.set(ModalTypes.Failure);
    } else {
      this.memberListInfoModalRef.modalType.set(ModalTypes.Success);
      this.selectionItems.set([]);
      this.loadMembers();
    }

    this.isSubmitting = false;
    this.memberListInfoModalRef.message.set(response.message || MembersMessagingConstants.MEMBERS_UNINVITE_SUCCESS());
    this.bootstrapService.manageModal("#memberListInfoModal", ModalActions.Show);
    this.bootstrapService.manageModal("#memberListConfirmationModal", ModalActions.Hide);
  }

  public cancelMemberUninviteConfirmation() {
    this.bootstrapService.manageModal("#memberListConfirmationModal", ModalActions.Hide);
  }
  // #endregion Uninvite Member

  // #region Bulk Actions
  public confirmBulkAction(bulkAction: MemberBulkActions): void {
    this.setBulkActionConfirmationModalFunctions();

    this.memberBulkAction = bulkAction;

    this.resetConfirmationModal();
    this.memberListConfirmationModalRef.modalType.set(ModalTypes.Question);

    // Invite / Uninvite use dedicated status-eligibility copy and skip the note + self-exclusion logic.
    if (bulkAction === MemberBulkActions.Invite || bulkAction === MemberBulkActions.Uninvite) {
      this.memberListConfirmationModalRef.message.set(
        bulkAction === MemberBulkActions.Invite
          ? MembersMessagingConstants.MEMBERS_BULK_INVITE_CONFIRMATION()
          : MembersMessagingConstants.MEMBERS_BULK_UNINVITE_CONFIRMATION());
      this.bootstrapService.manageModal("#memberListConfirmationModal", ModalActions.Show);
      return;
    }

    this.memberListConfirmationModalRef.enableNotes.set(true);

    if (bulkAction === MemberBulkActions.Delete) {
      this.memberListConfirmationModalRef.enableTypeToConfirm.set(true);
      this.memberListConfirmationModalRef.typeToConfirmText.set(MembersMessagingConstants.MEMBERS_BULK_DELETE_TYPE_TO_CONFIRM());
    }

    let actionText = '';
    switch (bulkAction) {
      case MemberBulkActions.Activate:
        actionText = 'activate';
        break;
      case MemberBulkActions.Deactivate:
        actionText = 'deactivate';
        break;
      case MemberBulkActions.Suspend:
        actionText = 'suspend';
        break;
      case MemberBulkActions.Unsuspend:
        actionText = 'unsuspend';
        break;
      case MemberBulkActions.Delete:
        actionText = 'delete';
        break;
    }

    const confirmationMessageData = {actionText: actionText, selectedCount: this.selectedCount()};
    let confirmationMessage = MembersMessagingConstants.MEMBERS_BULK_ACTION_CONFIRMATION().replaceValuesWithObject(confirmationMessageData);

    // A restricted user can't deactivate/delete their own account (the API skips it); note it alongside the count.
    const isRemoval = bulkAction === MemberBulkActions.Deactivate || bulkAction === MemberBulkActions.Delete;
    const restrictedFromOwn = bulkAction === MemberBulkActions.Delete ? !this.canDeleteOwnAccount() : !this.canDeactivateOwnAccount();
    if (isRemoval && restrictedFromOwn) {
      const pastTense = bulkAction === MemberBulkActions.Delete ? 'deleted' : 'deactivated';
      confirmationMessage += ' ' + MembersMessagingConstants.MEMBERS_BULK_SELF_EXCLUDED_NOTE().replaceValuesWithObject({ actionText: pastTense });
    }

    this.memberListConfirmationModalRef.message.set(confirmationMessage);
    this.bootstrapService.manageModal("#memberListConfirmationModal", ModalActions.Show);
  }

  public setBulkActionConfirmationModalFunctions() {
    this.confirmationConfirmFunction = this.bulkActionModalConfirmed;
    this.confirmationCancelFunction = this.bulkActionModalCancelled;
  }

  public async bulkActionModalConfirmed(note: string | null) : Promise<void> {
    this.isSubmitting = true;

    let bulkActionRequest: BulkActionRequest<MembersFilter | null> =
      {
        useFilter: this.isSelectAll,
        filter: (this.isSelectAll === true) ? this.userSessionService.membersFilter : null,
        selectedIds: (this.isSelectAll === false) ? this.selectionItems() : null,
        excludedIds: (this.isSelectAll === true) ? this.selectionItems() : null,
        note: note
      };

    let response: ServiceResultModel<BulkActionResponse>;
    switch (this.memberBulkAction) {
      case MemberBulkActions.Activate:
        response = await this.memberService.bulkUpdateMemberStatuses(MemberStatusActions.Activate, bulkActionRequest);
        break;
      case MemberBulkActions.Deactivate:
        response = await this.memberService.bulkUpdateMemberStatuses(MemberStatusActions.Deactivate, bulkActionRequest);
        break;
      case MemberBulkActions.Suspend:
        response = await this.memberService.bulkUpdateMemberStatuses(MemberStatusActions.Suspend, bulkActionRequest);
        break;
      case MemberBulkActions.Unsuspend:
        response = await this.memberService.bulkUpdateMemberStatuses(MemberStatusActions.Unsuspend, bulkActionRequest);
        break;
      case MemberBulkActions.Delete:
        response = await this.memberService.bulkDeleteMembers(bulkActionRequest);
        break;
      case MemberBulkActions.Invite:
        response = await this.memberService.bulkSendInvitations(bulkActionRequest);
        break;
      case MemberBulkActions.Uninvite:
        response = await this.memberService.bulkCancelInvitations(bulkActionRequest);
        break;
      default:
        response = {success: false, message: ErrorMessages.DEFAULT_FAILURE_MESSAGE()};
    }

    if (response.success === false) {
      //Error response received
      this.memberListInfoModalRef.modalType.set(ModalTypes.Failure);
    } else {
      //No error response
      this.memberListInfoModalRef.modalType.set(ModalTypes.Success);
      this.selectionItems.set([]);
      this.loadMembers();
    }

    this.isSubmitting = false;
    // On a successful removal, note the caller's own account was skipped — unless they may act on it.
    const isRemoval = this.memberBulkAction === MemberBulkActions.Deactivate ||
                      this.memberBulkAction === MemberBulkActions.Delete;
    const restrictedFromOwn = this.memberBulkAction === MemberBulkActions.Delete
      ? !this.canDeleteOwnAccount()
      : !this.canDeactivateOwnAccount();
    const selfNote = (response.success !== false && isRemoval && restrictedFromOwn)
      ? ' ' + MembersMessagingConstants.MEMBERS_BULK_SELF_EXCLUDED_NOTE()
          .replaceValuesWithObject({ actionText: this.memberBulkAction === MemberBulkActions.Delete ? 'deleted' : 'deactivated' })
      : '';
    this.memberListInfoModalRef.message.set((response.message ?? '') + selfNote);
    this.bootstrapService.manageModal("#memberListInfoModal", ModalActions.Show);
    this.bootstrapService.manageModal("#memberListConfirmationModal", ModalActions.Hide);
  }

  public bulkActionModalCancelled(){
    this.bootstrapService.manageModal("#memberListConfirmationModal", ModalActions.Hide);
  }
  // #endregion Bulk Action

  private resetConfirmationModal(): void {
    this.memberListConfirmationModalRef.enableNotes.set(false);
    this.memberListConfirmationModalRef.isNoteRequired.set(false);
    this.memberListConfirmationModalRef.enableTypeToConfirm.set(false);
    this.memberListConfirmationModalRef.confirmButtonText.set('Yes');
    this.memberListConfirmationModalRef.cancelButtonText.set('No');
  }

  //#region table scrolling
  public loadMoreInView(): void {       
    if (this.canLoadMore && this.hasNext) {
      this.loadMore();
      this.canLoadMore = false;
      this.loadMoreDebounce();
    }
  }
  //endregion
}
